import type { TranscriptSnippet } from "@expert/shared-types";
import type { JSX } from "react";

import { useVerifiedCustomerStore } from "@expert/sales-tools";
import { Flex, Text } from "@mantine/core";
import clsx from "clsx";
import { Virtuoso } from "react-virtuoso";

import { useSessionId } from "@/sdk";
import { TranscriptFooter } from "@/transcript/TranscriptFooter";
import { getTranscriptTimestamp } from "@/transcript/utils/getTranscriptTimestamp";

import styles from "./RedesignedTranscriptBody.module.css";

/**
 * Rendered once as the Virtuoso list footer (not per item) so the looping "Transcribing…" Lottie
 * mounts a single time per call. Anchoring it to the last item's `itemContent` made Virtuoso
 * unmount/remount lottie-web on every appended snippet, churning native/compositor memory.
 */
function TranscribingFooter(): JSX.Element {
    return (
        <div className={styles.footer}>
            <TranscriptFooter />
        </div>
    );
}

/**
 * Redesigned transcript body. Fills its parent (height: 100%) rather than the legacy rail's
 * viewport-relative `calc(100vh - ...)`. Kept separate so redesign styling can't leak into legacy/mcafee.
 */
export function RedesignedTranscriptBody({
    snippets,
    taskStartTimeInMs,
    showTranscribing = true,
}: {
    taskStartTimeInMs: number;
    snippets?: TranscriptSnippet[];
    showTranscribing?: boolean;
}): JSX.Element | undefined {
    const sessionId = useSessionId();
    const firstName = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer?.firstName);

    return (
        <Virtuoso
            initialTopMostItemIndex={snippets ? snippets.length - 1 : 0}
            atBottomThreshold={60}
            components={{ Footer: showTranscribing && snippets?.length ? TranscribingFooter : undefined }}
            followOutput={(isAtBottom) => (isAtBottom ? "auto" : false)}
            className={styles.scroller}
            data={snippets}
            itemContent={(index, snippet) => (
                <Flex
                    className={clsx(styles.item, index === 0 && styles.itemFirst)}
                    pb="xl"
                    direction="column"
                    key={snippet.content.concat(snippet.timestamp.toString())}
                >
                    <Flex w="100%" justify="space-between">
                        <Text c={snippet.speaker === "Expert" ? "dark.2" : "primary.2"} size="sm">
                            {snippet.speaker === "Expert" ? "You" : (firstName ?? "Customer")}
                        </Text>
                        <Text c="dark.2" size="sm">
                            {getTranscriptTimestamp(taskStartTimeInMs, snippet.timestamp)}
                        </Text>
                    </Flex>
                    <Text className={styles.content} pt="3xs" c="dark.1" size="sm">
                        {snippet.content}
                    </Text>
                </Flex>
            )}
        ></Virtuoso>
    );
}
